import { useEffect, useRef, useState } from "react";

export interface PhaseConfig {
  key: string;
  weight: number;
}

const DEFAULT_PHASES: PhaseConfig[] = [
  { key: "read", weight: 35 },
  { key: "split", weight: 22 },
  { key: "prompt", weight: 32 },
  { key: "draft", weight: 28 },
];

/** Time constant for the asymptotic progress curve (seconds). */
const ASYMPTOTIC_TAU_S = 40;

/** Reference duration used for ETA label calculation (seconds). */
const ETA_REFERENCE_DURATION_S = 60;

/** Upper bound to prevent floating-point from reaching exactly 1. */
const MAX_NORM = 0.999;

/**
 * Time (in ms) over which `norm` lerps toward a new `dataAnchor` value.
 * Keeps the progress bar visually smooth between polls.
 */
const DATA_ANCHOR_LERP_MS = 200;

interface PhaseProgress {
  phaseIdx: number;
  intra: number;
  remaining: number;
}

interface UsePhaseProgressOptions {
  /**
   * If provided, `norm` is smoothly interpolated toward this value (0..1)
   * instead of being driven by the asymptotic time curve. Useful when real
   * data is available to anchor progress to.
   */
  dataAnchor?: number;
}

/**
 * Drives the segmented progress bar in the FTUE template-build UI.
 *
 * Two modes:
 * - When `options.dataAnchor` is **undefined**, `norm` follows an asymptotic
 *   time curve (`1 - exp(-t/τ)`). This is the legacy "synthetic" mode.
 * - When `options.dataAnchor` is provided, `norm` tracks it via a short
 *   linear interpolation (so the bar animates smoothly between polled
 *   snapshots), and the time elapsed is used only for the ETA label.
 */
export const usePhaseProgress = (
  phases: PhaseConfig[] = DEFAULT_PHASES,
  options: UsePhaseProgressOptions = {}
): PhaseProgress => {
  const { dataAnchor } = options;
  const isDataDriven = typeof dataAnchor === "number";

  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(performance.now());
  const rafRef = useRef<number | null>(null);

  // Smoothly tracked progress in [0, 1] used when running data-anchored.
  const [smoothNorm, setSmoothNorm] = useState(
    isDataDriven ? Math.max(0, Math.min(1, dataAnchor ?? 0)) : 0
  );
  const lerpStartRef = useRef<{ from: number; at: number } | null>(null);
  const targetRef = useRef<number>(
    isDataDriven ? Math.max(0, Math.min(1, dataAnchor ?? 0)) : 0
  );

  // When dataAnchor changes, start a new lerp from the current smoothNorm.
  useEffect(() => {
    if (!isDataDriven) {
      lerpStartRef.current = null;
      return;
    }

    const clamped = Math.max(0, Math.min(1, dataAnchor ?? 0));
    targetRef.current = clamped;
    lerpStartRef.current = {
      from: smoothNorm,
      at: performance.now(),
    };
    // We intentionally do not depend on `smoothNorm` to avoid retriggering
    // the lerp on every animation frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAnchor, isDataDriven]);

  useEffect(() => {
    const tick = (now: number) => {
      setElapsed((now - startRef.current) / 1000);

      if (isDataDriven) {
        const lerp = lerpStartRef.current;
        if (lerp) {
          const t = Math.min(1, (now - lerp.at) / DATA_ANCHOR_LERP_MS);
          const next = lerp.from + (targetRef.current - lerp.from) * t;
          setSmoothNorm(next);
          if (t >= 1) {
            lerpStartRef.current = null;
          }
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isDataDriven]);

  const norm = isDataDriven
    ? Math.min(smoothNorm, MAX_NORM)
    : Math.min(1 - Math.exp(-elapsed / ASYMPTOTIC_TAU_S), MAX_NORM);

  const totalWeight = phases.reduce((s, p) => s + p.weight, 0);
  let acc = 0;
  let idx = 0;
  let intra = 0;
  for (let i = 0; i < phases.length; i++) {
    const w = phases[i].weight / totalWeight;
    if (norm < acc + w || i === phases.length - 1) {
      idx = i;
      intra = Math.min((norm - acc) / w, 1);
      break;
    }
    acc += w;
  }

  return {
    phaseIdx: idx,
    intra,
    remaining: Math.max(0, ETA_REFERENCE_DURATION_S - elapsed),
  };
};
